import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { format } from "date-fns";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { Spinner } from "src/core/components/custom-ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/core/components/ui/table";
import { IMission } from "src/core/hooks/folder/useFolderMissions";

type MissionsDataTableProps = {
  columns: ColumnDef<IMission>[];
  data: IMission[];
  isLoading?: boolean;
  noResultText?: string;
  className?: string;
};

export const MissionsDataTable = ({
  columns,
  data,
  isLoading = false,
  className,
}: MissionsDataTableProps) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
  });
  const { t } = useTranslation();

  return (
    <div className="missions-table-wrapper">
      {isLoading ? (
        <Table className="missions-table">
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                <div className="flex items-center justify-center">
                  <Spinner size="lg" />
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        table.getRowModel().rows?.length && (
          <Table
            className={`missions-table ${className}`}
            style={{ tableLayout: "fixed", width: "100%" }}
          >
            <TableHeader className="missions-table-header">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} style={{ width: header.getSize() }}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    className="missions-table-row"
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        className="missions-table-cell"
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.original.statusComment && (
                    <TableRow key={`${row.id}-comment`}>
                      <TableCell colSpan={columns.length} className="h-auto pt-0">
                        <div className="missions-table-status-comment">
                          <div className="font-medium uppercase mb-5">
                            {t("folder.missions.data.statusComment", {
                              missionDate: row.original.lastModification
                                ? format(new Date(row.original.lastModification), "dd/MM/yyyy")
                                : "",
                            })}
                          </div>
                          <div className="font-light">{row.original.statusComment}</div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )
      )}
    </div>
  );
};
